import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Search, FileText, Upload, Download, Calendar, Clock, MapPin, Users, Eye, Paperclip } from 'lucide-react';
import { getCRMMomList, getCRMMom, createCRMMom, updateCRMMom, deleteCRMMom, uploadCRMMomAttachments, getCRMContacts, getCRMDeals } from '../api';

const TIPE_RAPAT_OPTIONS = [
  { value: 'rapat', label: 'Rapat Tim' },
  { value: 'kunjungan', label: 'Kunjungan Klien' },
  { value: 'presentasi', label: 'Presentasi' },
  { value: 'negosiasi', label: 'Negosiasi' },
  { value: 'koordinasi', label: 'Koordinasi Internal' },
  { value: 'lainnya', label: 'Lainnya' },
];

const STATUS_OPTIONS = [
  { value: 'tercatat', label: 'Tercatat' },
  { value: 'sedang_ditindaklanjuti', label: 'Sedang Ditindaklanjuti' },
  { value: 'selesai', label: 'Selesai' },
];

// Helper format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Helper get file icon color based on type
const getFileColor = (fileType) => {
  if (!fileType) return '#6b7280';
  if (fileType.includes('pdf')) return '#f5365c';
  if (fileType.includes('image')) return '#11cdef';
  if (fileType.includes('word') || fileType.includes('document')) return '#2dce89';
  if (fileType.includes('sheet') || fileType.includes('excel')) return '#fb6340';
  if (fileType.includes('presentation') || fileType.includes('powerpoint')) return '#f59e0b';
  return '#6b7280';
};

// ========= MOM FORM MODAL =========
function MOMFormModal({ show, onClose, onSave, contacts, deals, editData }) {
  const [form, setForm] = useState({
    judul: '', tanggal: '', waktu_mulai: '', waktu_selesai: '',
    lokasi: '', peserta: '', contact_id: null, deal_id: null,
    tipe_rapat: 'rapat', ringkasan: '', tindak_lanjut: '', catatan: '',
  });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  useEffect(() => {
    if (editData) {
      setForm({
        judul: editData.judul || '',
        tanggal: editData.tanggal ? editData.tanggal.substring(0, 10) : '',
        waktu_mulai: editData.waktu_mulai || '',
        waktu_selesai: editData.waktu_selesai || '',
        lokasi: editData.lokasi || '',
        peserta: editData.peserta || '',
        contact_id: editData.contact_id || null,
        deal_id: editData.deal_id || null,
        tipe_rapat: editData.tipe_rapat || 'rapat',
        ringkasan: editData.ringkasan || '',
        tindak_lanjut: editData.tindak_lanjut || '',
        catatan: editData.catatan || '',
      });
    } else {
      setForm({
        judul: '', tanggal: new Date().toISOString().substring(0, 10),
        waktu_mulai: '', waktu_selesai: '',
        lokasi: '', peserta: '', contact_id: null, deal_id: null,
        tipe_rapat: 'rapat', ringkasan: '', tindak_lanjut: '', catatan: '',
      });
    }
    setFiles([]);
    setUploadProgress('');
  }, [editData, show]);

  const handleSubmit = async () => {
    if (!form.judul.trim()) return alert('Judul wajib diisi');
    if (!form.tanggal) return alert('Tanggal wajib diisi');
    setSaving(true);
    try {
      let savedId = editData?.id;
      if (editData) {
        await updateCRMMom(editData.id, form);
        savedId = editData.id;
      } else {
        const res = await createCRMMom(form);
        savedId = res.data.id;
      }

      // Upload files if any
      if (files.length > 0 && savedId) {
        setUploadProgress(`Mengupload ${files.length} file...`);
        const formData = new FormData();
        files.forEach(f => formData.append('files', f));
        await uploadCRMMomAttachments(savedId, formData);
      }

      onSave();
    } catch (e) {
      alert('Gagal menyimpan: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
      setUploadProgress('');
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb',
    borderRadius: 6, fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontWeight: 600, fontSize: '0.82rem', marginBottom: 4, display: 'block', color: '#374151',
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}
      style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 12, width: '95%', maxWidth: 800, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
          <div style={{ color: '#fff' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>{editData ? 'Edit MOM' : 'Catatan MOM Baru'}</h3>
            <small style={{ opacity: 0.85, fontSize: '0.78rem' }}>Minutes of Meeting — Hasil Rapat / Kegiatan Tim CRM</small>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#fff' }}><X size={18} /></button>
        </div>

        <div style={{ padding: '20px' }}>
          {/* Row 1: Judul & Tipe Rapat */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Judul Rapat *</label>
              <input style={inputStyle} value={form.judul} onChange={e => setForm({ ...form, judul: e.target.value })} placeholder="Contoh: Rapat Evaluasi Q2 dengan PT KWK" />
            </div>
            <div>
              <label style={labelStyle}>Tipe Rapat</label>
              <select style={inputStyle} value={form.tipe_rapat} onChange={e => setForm({ ...form, tipe_rapat: e.target.value })}>
                {TIPE_RAPAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Tanggal, Waktu Mulai, Waktu Selesai */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Tanggal *</label>
              <input type="date" style={inputStyle} value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Waktu Mulai</label>
              <input type="time" style={inputStyle} value={form.waktu_mulai} onChange={e => setForm({ ...form, waktu_mulai: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Waktu Selesai</label>
              <input type="time" style={inputStyle} value={form.waktu_selesai} onChange={e => setForm({ ...form, waktu_selesai: e.target.value })} />
            </div>
          </div>

          {/* Row 3: Lokasi & Peserta */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Lokasi</label>
              <input style={inputStyle} value={form.lokasi} onChange={e => setForm({ ...form, lokasi: e.target.value })} placeholder="Contoh: Kantor Pusat / Virtual" />
            </div>
            <div>
              <label style={labelStyle}>Peserta</label>
              <input style={inputStyle} value={form.peserta} onChange={e => setForm({ ...form, peserta: e.target.value })} placeholder="Contoh: Budi, Andi, Rina (PT KWK)" />
            </div>
          </div>

          {/* Row 4: Contact & Deal */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Kontak Terkait</label>
              <select style={inputStyle} value={form.contact_id || ''} onChange={e => setForm({ ...form, contact_id: e.target.value ? parseInt(e.target.value) : null })}>
                <option value="">-- Pilih Kontak (opsional) --</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.nama}{c.perusahaan ? ` (${c.perusahaan})` : ''}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Deal Terkait</label>
              <select style={inputStyle} value={form.deal_id || ''} onChange={e => setForm({ ...form, deal_id: e.target.value ? parseInt(e.target.value) : null })}>
                <option value="">-- Pilih Deal (opsional) --</option>
                {deals.map(d => <option key={d.id} value={d.id}>{d.judul}</option>)}
              </select>
            </div>
          </div>

          {/* Ringkasan */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Ringkasan / Isi Notulensi</label>
            <textarea style={{ ...inputStyle, minHeight: 220, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
              value={form.ringkasan} onChange={e => setForm({ ...form, ringkasan: e.target.value })}
              placeholder="Tuliskan poin-poin pembahasan, keputusan, dan hasil rapat..." />
          </div>

          {/* Tindak Lanjut */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Tindak Lanjut (Action Items)</label>
            <textarea style={{ ...inputStyle, minHeight: 160, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
              value={form.tindak_lanjut} onChange={e => setForm({ ...form, tindak_lanjut: e.target.value })}
              placeholder="Contoh:&#10;- Budi: Kirim penawaran harga ke PT KWK (deadline: 30 Jul)&#10;- Andi: Follow up jadwal survey armada" />
          </div>

          {/* Catatan */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Catatan Tambahan</label>
            <textarea style={{ ...inputStyle, minHeight: 140, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
              value={form.catatan} onChange={e => setForm({ ...form, catatan: e.target.value })}
              placeholder="Catatan tambahan (opsional)..." />
          </div>

          {/* File Upload */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Dokumen / Lampiran</label>
            <div style={{ border: '2px dashed #d1d5db', borderRadius: 8, padding: 16, textAlign: 'center', background: '#f9fafb', cursor: 'pointer', transition: 'border-color 0.2s' }}
              onClick={() => document.getElementById('mom-file-input').click()}>
              <Upload size={24} style={{ color: '#9ca3af', marginBottom: 6 }} />
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#6b7280' }}>Klik untuk memilih file atau drag & drop</p>
              <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: '#9ca3af' }}>PDF, Gambar, Dokumen Word/Excel (max 20MB per file)</p>
              <input id="mom-file-input" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                style={{ display: 'none' }} onChange={handleFileChange} />
            </div>
            {files.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {files.map((f, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: '#f3f4f6', borderRadius: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: '0.78rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      <Paperclip size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />{f.name} <span style={{ color: '#9ca3af' }}>({formatFileSize(f.size)})</span>
                    </span>
                    <button onClick={() => removeFile(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f5365c', padding: 2 }}><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
            {uploadProgress && <p style={{ fontSize: '0.78rem', color: '#667eea', marginTop: 6 }}>{uploadProgress}</p>}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
            <button onClick={onClose} style={{ padding: '8px 18px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', fontSize: '0.85rem', cursor: 'pointer' }}>Batal</button>
            <button onClick={handleSubmit} disabled={saving}
              style={{ padding: '8px 18px', border: 'none', borderRadius: 8, background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Menyimpan...' : (editData ? 'Update MOM' : 'Simpan MOM')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========= MOM DETAIL MODAL =========
function MOMDetailModal({ show, onClose, item, _onRefresh }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && item?.id) {
      setLoading(true);
      getCRMMom(item.id).then(res => {
        setDetail(res.data);
        setLoading(false);
      }).catch(() => {
        setDetail(item);
        setLoading(false);
      });
    }
  }, [show, item]);

  const handleDownloadFile = (fileURL, filename) => {
    const link = document.createElement('a');
    link.href = fileURL;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!show) return null;
  const data = detail || item;

  return (
    <div className="modal-overlay" onClick={onClose}
      style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 12, width: '95%', maxWidth: 700, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
          <div style={{ color: '#fff' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>{data.judul || 'Detail MOM'}</h3>
            <small style={{ opacity: 0.85, fontSize: '0.78rem' }}>{TIPE_RAPAT_OPTIONS.find(t => t.value === data.tipe_rapat)?.label || data.tipe_rapat}</small>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#fff' }}><X size={18} /></button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Memuat detail...</div>
        ) : (
          <div style={{ padding: '20px' }}>
            {/* Info Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <InfoItem icon={<Calendar size={14} />} label="Tanggal" value={data.tanggal ? new Date(data.tanggal).toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '-'} />
              <InfoItem icon={<Clock size={14} />} label="Waktu" value={`${data.waktu_mulai || '-'} — ${data.waktu_selesai || '-'}`} />
              <InfoItem icon={<MapPin size={14} />} label="Lokasi" value={data.lokasi || '-'} />
              <InfoItem icon={<Users size={14} />} label="Peserta" value={data.peserta || '-'} />
              {data.contact_nama && <InfoItem icon={<Users size={14} />} label="Kontak" value={data.contact_nama} />}
              {data.deal_judul && <InfoItem icon={<FileText size={14} />} label="Deal" value={data.deal_judul} />}
            </div>

            {/* Status Badge */}
            <div style={{ marginBottom: 14 }}>
              <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
                background: data.status === 'selesai' ? '#d1fae5' : data.status === 'sedang_ditindaklanjuti' ? '#fef3c7' : '#e0e7ff',
                color: data.status === 'selesai' ? '#065f46' : data.status === 'sedang_ditindaklanjuti' ? '#92400e' : '#3730a3' }}>
                {STATUS_OPTIONS.find(s => s.value === data.status)?.label || data.status}
              </span>
            </div>

            {/* Ringkasan */}
            {data.ringkasan && (
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: 6 }}>Ringkasan / Notulensi</h4>
                <div style={{ padding: '12px', background: '#f9fafb', borderRadius: 8, fontSize: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{data.ringkasan}</div>
              </div>
            )}

            {/* Tindak Lanjut */}
            {data.tindak_lanjut && (
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: 6 }}>Tindak Lanjut</h4>
                <div style={{ padding: '12px', background: '#fffbeb', borderRadius: 8, fontSize: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', border: '1px solid #fde68a' }}>{data.tindak_lanjut}</div>
              </div>
            )}

            {/* Catatan */}
            {data.catatan && (
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: 6 }}>Catatan</h4>
                <div style={{ padding: '12px', background: '#f3f4f6', borderRadius: 8, fontSize: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{data.catatan}</div>
              </div>
            )}

            {/* Attachments */}
            {data.attachments && data.attachments.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: 6 }}><Paperclip size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Lampiran ({data.attachments.length})</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {data.attachments.map(att => (
                    <div key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, overflow: 'hidden' }}>
                        <Paperclip size={14} style={{ color: getFileColor(att.file_type), flexShrink: 0 }} />
                        <span style={{ fontSize: '0.82rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.filename}</span>
                        <span style={{ fontSize: '0.72rem', color: '#9ca3af', flexShrink: 0 }}>({formatFileSize(att.file_size)})</span>
                      </div>
                      <button onClick={() => handleDownloadFile(att.file_url, att.filename)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#667eea', padding: 4, flexShrink: 0 }}>
                        <Download size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Created info */}
            <div style={{ paddingTop: 12, borderTop: '1px solid #f3f4f6', fontSize: '0.72rem', color: '#9ca3af' }}>
              Dibuat oleh {data.created_nama || '-'} • {data.created_at || '-'}
              {data.updated_at && data.updated_at !== data.created_at && ` • Diupdate: ${data.updated_at}`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoItem({ icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', background: '#f9fafb', borderRadius: 6 }}>
      <span style={{ color: '#667eea', marginTop: 1 }}>{icon}</span>
      <div>
        <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: '0.82rem', color: '#374151', fontWeight: 500 }}>{value}</div>
      </div>
    </div>
  );
}

// ========= MAIN MOM TAB =========
export default function MOMTab({ onCountChange }) {
  const [moms, setMoms] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTipe, setFilterTipe] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadMoms = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterTipe) params.tipe_rapat = filterTipe;
      if (filterStatus) params.status = filterStatus;
      const res = await getCRMMomList(params);
      const list = Array.isArray(res.data) ? res.data : [];
      setMoms(list);
      if (onCountChange) onCountChange(list.length);
    } catch (e) {
      console.error('Gagal load MOM:', e);
    } finally {
      setLoading(false);
    }
  }, [search, filterTipe, filterStatus]);

  const loadLookups = useCallback(async () => {
    try {
      const [cRes, dRes] = await Promise.all([getCRMContacts(), getCRMDeals()]);
      setContacts(Array.isArray(cRes.data) ? cRes.data : []);
      setDeals(Array.isArray(dRes.data) ? dRes.data : []);
    } catch (e) {
      console.error('Gagal load lookups:', e);
    }
  }, []);

  useEffect(() => { loadMoms(); }, [loadMoms]);
  useEffect(() => { loadLookups(); }, [loadLookups]);

  const handleSave = () => {
    setShowForm(false);
    setEditData(null);
    loadMoms();
  };

  const handleDelete = async (id) => {
    try {
      await deleteCRMMom(id);
      setDeleteConfirm(null);
      loadMoms();
    } catch (e) {
      alert('Gagal hapus: ' + (e.response?.data?.error || e.message));
    }
  };

  const getTipeLabel = (val) => TIPE_RAPAT_OPTIONS.find(o => o.value === val)?.label || val;
  const getStatusStyle = (val) => {
    switch (val) {
      case 'selesai': return { bg: '#d1fae5', color: '#065f46' };
      case 'sedang_ditindaklanjuti': return { bg: '#fef3c7', color: '#92400e' };
      default: return { bg: '#e0e7ff', color: '#3730a3' };
    }
  };
  const getStatusLabel = (val) => STATUS_OPTIONS.find(o => o.value === val)?.label || val;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#1f2937' }}>Minutes of Meeting (MOM)</h3>
          <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#6b7280' }}>Catatan hasil rapat dan kegiatan Tim CRM</p>
        </div>
        <button onClick={() => { setEditData(null); setShowForm(true); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: 'none', borderRadius: 8,
            background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
          <Plus size={16} /> Catatan Baru
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0 10px', flex: '1 1 200px' }}>
          <Search size={14} style={{ color: '#9ca3af' }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Cari judul, ringkasan, peserta..."
            style={{ border: 'none', outline: 'none', padding: '8px', fontSize: '0.85rem', width: '100%', background: 'transparent' }} />
        </div>
        <select value={filterTipe} onChange={e => setFilterTipe(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: '0.85rem', background: '#fff', minWidth: 140 }}>
          <option value="">Semua Tipe</option>
          {TIPE_RAPAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: '0.85rem', background: '#fff', minWidth: 140 }}>
          <option value="">Semua Status</option>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Judul</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Tanggal</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Tipe</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Peserta</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Status</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Kontak</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>Memuat data...</td></tr>
            ) : moms.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>Belum ada catatan MOM. Klik "Catatan Baru" untuk menambah.</td></tr>
            ) : moms.map((m, idx) => {
              const ss = getStatusStyle(m.status);
              return (
                <tr key={m.id || idx} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontWeight: 600, color: '#1f2937', fontSize: '0.85rem' }}>{m.judul}</div>
                    {m.lokasi && <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 2 }}><MapPin size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} />{m.lokasi}</div>}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#374151' }}>{m.tanggal ? new Date(m.tanggal).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 10, background: '#f3f4f6', color: '#6b7280' }}>{getTipeLabel(m.tipe_rapat)}</span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: '0.78rem', color: '#374151', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.peserta || '-'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px', borderRadius: 12, background: ss.bg, color: ss.color }}>
                      {getStatusLabel(m.status)}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: '0.78rem', color: '#374151' }}>{m.contact_nama || '-'}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button title="Lihat Detail" onClick={() => setDetailItem(m)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#667eea', padding: 4 }}><Eye size={14} /></button>
                    <button title="Edit" onClick={() => { setEditData(m); setShowForm(true); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f59e0b', padding: 4, marginLeft: 4 }}><Edit2 size={14} /></button>
                    <button title="Hapus" onClick={() => setDeleteConfirm(m)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f5365c', padding: 4, marginLeft: 4 }}><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div style={{ marginTop: 10, fontSize: '0.75rem', color: '#9ca3af' }}>
        Menampilkan {moms.length} catatan MOM
      </div>

      {/* Form Modal */}
      <MOMFormModal show={showForm} onClose={() => { setShowForm(false); setEditData(null); }} onSave={handleSave}
        contacts={contacts} deals={deals} editData={editData} />

      {/* Detail Modal */}
      <MOMDetailModal show={!!detailItem} onClose={() => setDetailItem(null)} item={detailItem} onRefresh={loadMoms} />

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}
          style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: 400, padding: 0, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #f5365c, #dc2626)', color: '#fff' }}>
              <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Hapus Catatan MOM</h4>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ fontSize: '0.85rem', color: '#374151', margin: '0 0 16px' }}>
                Yakin ingin menghapus <strong>"{deleteConfirm.judul}"</strong>? Semua lampiran terkait juga akan dihapus.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button onClick={() => setDeleteConfirm(null)}
                  style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', fontSize: '0.85rem', cursor: 'pointer' }}>Batal</button>
                <button onClick={() => handleDelete(deleteConfirm.id)}
                  style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#f5365c', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>Hapus</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
